import { formOptions } from "@tanstack/react-form";
import { zodValidator } from "@tanstack/zod-form-adapter";
import { z } from "zod";

import { AddressSchema } from "@/schemas/common/address";

import type { DeliveryFormValues } from "./types";
import { ADDRESS_DEFAULTS, VEHICLE_DEFAULTS } from "./types";

// ─── Per-Step Zod Schemas (used by FormGroup validators) ────────────────────

export const step0Schema = z.object({
  clientFirstName: z.string().min(1, "First name is required"),
  clientLastName: z.string().min(1, "Last name is required"),
  clientEmail: z.string().email("Invalid email"),
  clientPhone: z.string().min(1, "Phone is required"),
  clientCompany: z.string(),
  clientSiret: z.string(),
  clientDrivingLicenceNumber: z.string(),
  selectedMandataireId: z.string(),
  selectedOrganizationId: z.string(),
  representativeFirstName: z.string().min(1, "Representative first name is required"),
  representativeLastName: z.string().min(1, "Representative last name is required"),
  representativeEmail: z.string().email("Invalid representative email"),
  representativePhone: z.string().min(1, "Representative phone is required"),
  representativeCompany: z.string(),
  representativeSiret: z.string().optional(),
  carAvailabilityDate: z.string().min(1, "Car availability date is required"),
  carPickUpDate: z.string().min(1, "Car pickup date is required"),
  carDeliveryDeadline: z.string().min(1, "Delivery deadline is required"),
});

const vehicleSchema = z.object({
  carType: z.enum(["A", "B", "C"]).or(z.literal("")),
  type: z.string().min(1, "Motorization is required"),
  vin: z.string(),
  vehicleCode: z.string().min(1, "License plate is required"),
  marque: z.string().min(1, "Brand is required"),
  model: z.string().min(1, "Model is required"),
});

export const step1Schema = z.object({
  fromAddress: AddressSchema,
  toAddress: AddressSchema,
  vehicle: vehicleSchema,
});

export const step2Schema = z.object({
  toReturnFromAddress: AddressSchema,
  toReturnToAddress: AddressSchema,
  returnVehicle: vehicleSchema,
});

export const lastStepSchema = z.object({
  category: z.enum(["STANDARD", "OKM", "EXPRESS"]),
  selectedOptions: z.object({
    selected: z.array(
      z.object({
        id: z.string(),
        name: z.string(),
        price: z.number(),
        photo: z.string().optional(),
      })
    ),
  }),
  comment: z.string(),
  docs: z
    .array(
      z.object({
        label: z.string().optional(),
        key: z.string(),
        type: z.string().optional(),
        url: z.string(),
        name: z.string().optional(),
      })
    )
    .optional(),
});

// ─── Default Values ─────────────────────────────────────────────────────────

export const FORM_DEFAULTS: DeliveryFormValues = {
  step0: {
    clientFirstName: "",
    clientLastName: "",
    clientEmail: "",
    clientPhone: "",
    clientCompany: "",
    clientSiret: "",
    clientDrivingLicenceNumber: "",
    selectedMandataireId: "",
    selectedOrganizationId: "",
    representativeFirstName: "",
    representativeLastName: "",
    representativeEmail: "",
    representativePhone: "",
    representativeCompany: "",
    representativeSiret: "",
    carAvailabilityDate: "",
    carPickUpDate: "",
    carDeliveryDeadline: "",
  },
  step1: {
    fromAddress: { ...ADDRESS_DEFAULTS },
    toAddress: { ...ADDRESS_DEFAULTS },
    vehicle: { ...VEHICLE_DEFAULTS },
  },
  step2: {
    toReturnFromAddress: { ...ADDRESS_DEFAULTS },
    toReturnToAddress: { ...ADDRESS_DEFAULTS },
    returnVehicle: { ...VEHICLE_DEFAULTS },
  },
  lastStep: {
    category: "STANDARD",
    selectedOptions: {
      selected: [] satisfies Array<{
        id: string;
        name: string;
        price: number;
        photo?: string;
      }>,
    },
    comment: "",
    docs: [] satisfies Array<{
      label?: string;
      key: string;
      type?: string;
      url: string;
      name?: string;
    }>,
  },
};

// ─── Form Options ───────────────────────────────────────────────────────────

export const deliveryFormOpts = formOptions({
  defaultValues: FORM_DEFAULTS,
  validatorAdapter: zodValidator(),
  validators: {
    onDynamic: z.object({
      step0: step0Schema,
      step1: step1Schema,
      step2: step2Schema,
      lastStep: lastStepSchema,
    }),
  },
});
